import { useState } from 'react';
import { Table, Button, Group, TextInput, Badge, Modal, Stack, Select, Divider, Alert, Avatar, Text } from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { useDisclosure, useDebouncedValue } from '@mantine/hooks';
import { IconPlus, IconUsers, IconUserPlus } from '@tabler/icons-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api, { fileUrl } from '../services/api';
import { PageHeader } from '../components/PageHeader';
import { TablePagination } from '../components/TablePagination';
import { EmptyState } from '../components/EmptyState';
import { TableSkeleton } from '../components/TableSkeleton';
import { PhoneInput } from '../components/PhoneInput';
import { CreatableSelect, ServerCreatableSelect, GenderInput, QuickClassForm, QuickParentForm } from '../components/QuickCreate';
import { ContractButton } from '../components/ContractGenerator';
import { MultiPhoneEditor, MultiAddressEditor, PhoneItem, AddressItem } from '../components/MultiContact';
import { useAuth } from '../contexts/AuthContext';
import { usePagedQuery } from '../hooks/usePagedQuery';
import { TableExport } from '../components/TableExport';
import { useClasses } from '../hooks/useLookups';
import { TableToolbar } from '../components/TableToolbar';
import { FetchOverlay } from '../components/FetchOverlay';
import { SectionCard } from '../components/SectionCard';
import { canAccess } from '../lib/permissions';
import { uzDateProps } from '../lib/dateInput';

interface Student {
  id: string;
  fullName: string;
  phone?: string;
  status: string;
  parentPhone?: string;
  photo?: string | null;
  class?: { id: string; name: string } | null;
}
interface Admission {
  id: string;
  applicantName: string;
  parentName?: string;
  parentPhone: string;
  birthDate?: string;
  status: string;
}

export default function Students() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const canManage = canAccess(user?.role, 'admissions'); // qabul = o'quvchi qo'sha oladiganlar (MANAGERS)
  // Shartnoma — ALOHIDA bo'lim (`lib/permissions.ts`): qabulni rolidan olib qo'yish
  // shartnoma yaratishni ham beixtiyor yopib qo'ymasligi kerak.
  const canContracts = canAccess(user?.role, 'contracts');
  const [search, setSearch] = useState('');
  const [q] = useDebouncedValue(search, 300);
  const [opened, { open, close }] = useDisclosure(false);
  const [admissionId, setAdmissionId] = useState<string | null>(null);

  const [sort, setSort] = useState('fullName:asc');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [classFilter, setClassFilter] = useState<string>('');
  /* Filtrlar BITTA joyda: jadval ham, XLSX eksporti ham AYNI obyektni oladi.
     Ikki nusxa bo'lsa ular vaqt o'tib ajralib ketardi va foydalanuvchi ekranda
     bir narsani ko'rib, faylda boshqasini olardi. */
  const filtrlar = {
    q: q || undefined,
    sort,
    status: statusFilter || undefined,
    classId: classFilter || undefined,
  };

  const { items: students, isLoading, fetching, total, pages, page, setPage, limit, setLimit} = usePagedQuery<Student>(
    'students', '/students', filtrlar
  );

  const { data: classes } = useClasses();

  // Qabuldagi (hali ro'yxatga olinmagan) nomzodlar
  const { data: admissions } = useQuery({
    queryKey: ['admissions-open'],
    queryFn: async () => (await api.get<Admission[]>('/admissions')).data,
    enabled: canManage && opened,
  });
  const pending = (admissions ?? []).filter((a) => a.status !== 'ENROLLED' && a.status !== 'REJECTED');

  const form = useForm({
    initialValues: { fullName: '', phone: '', classId: '', gender: '', parentId: '', parentName: '', parentPhone: '', birthDate: null as Date | null, phones: [] as PhoneItem[], addresses: [] as AddressItem[] },
    validate: {
      fullName: (v) => (v.trim().length < 3 ? t('studentsPage.validateFullName') : null),
      classId: (v) => (!v ? t('studentsPage.validateClass') : null),
      gender: (v) => (!v ? t('studentsPage.validateGender') : null),
    },
  });

  const resetAll = () => { form.reset(); setAdmissionId(null); };

  const pickAdmission = (id: string | null) => {
    setAdmissionId(id);
    const a = pending.find((x) => x.id === id);
    if (a) {
      form.setValues((prev) => ({
        ...prev,
        fullName: a.applicantName ?? '',
        parentName: a.parentName ?? '',
        parentPhone: a.parentPhone ?? '',
        birthDate: a.birthDate ? new Date(a.birthDate) : null,
      }));
    }
  };

  const createMut = useMutation({
    mutationFn: (v: typeof form.values) =>
      api.post('/students', { ...v, birthDate: v.birthDate ? v.birthDate.toISOString() : undefined }),
    onSuccess: async () => {
      // Qabuldan yaratilgan bo'lsa — o'sha murojaatni "ro'yxatga olindi" deb belgilaymiz
      if (admissionId) {
        try {
          await api.patch(`/admissions/${admissionId}`, { status: 'ENROLLED' });
          qc.invalidateQueries({ queryKey: ['admissions'] });
          qc.invalidateQueries({ queryKey: ['admissions-open'] });
        } catch {
          /* ixtiyoriy — asosiy amal bajarildi */
        }
      }
      notifications.show({ color: 'green', message: t('studentsPage.created') });
      qc.invalidateQueries({ queryKey: ['students'] });
      resetAll();
      close();
    },
  });

  return (
    <>
      <PageHeader
        title={t('pages.students.title')}
        subtitle={`${t('common.total')}: ${total}`}
        action={canManage ? <Button leftSection={<IconPlus size={18} />} onClick={open}>{t('pages.students.add')}</Button> : undefined}
      />

      <TableToolbar
        search={search}
        onSearch={(v) => { setSearch(v); setPage(1); }}
        searchPlaceholder={t('pages.students.search')}
        sort={sort}
        onSort={(v) => { setSort(v); setPage(1); }}
        sortOptions={[
          { value: 'fullName:asc', label: t('studentsPage.sortNameAsc') },
          { value: 'fullName:desc', label: t('studentsPage.sortNameDesc') },
          { value: 'createdAt:desc', label: t('studentsPage.sortNewest') },
          { value: 'admissionDate:desc', label: t('studentsPage.sortAdmissionDate') },
        ]}
      >
        <Select
          placeholder={t('fields.class')}
          data={(classes ?? []).map((c) => ({ value: c.id, label: c.name }))}
          value={classFilter}
          onChange={(v) => { setClassFilter(v ?? ''); setPage(1); }}
          searchable clearable w={130}
        />
        <Select
          placeholder={t('fields.status')}
          data={[
            { value: 'ACTIVE', label: t('studentStatus.ACTIVE') },
            { value: 'INACTIVE', label: t('studentStatus.INACTIVE') },
            { value: 'GRADUATED', label: t('studentStatus.GRADUATED') },
          ]}
          value={statusFilter}
          onChange={(v) => { setStatusFilter(v ?? ''); setPage(1); }}
          clearable w={140}
        />
          <TableExport<Student>
            endpoint="/students"
            params={filtrlar}
            filename={t('nav.students')}
            total={total}
            ustunlar={[
              { key: 'fullName', label: t('fields.fullName') },
              { key: 'class', label: t('fields.class'), get: (r) => r.class?.name ?? '' },
              { key: 'phone', label: t('fields.phone') },
              { key: 'parentPhone', label: t('studentsPage.colParentPhone') },
              { key: 'status', label: t('fields.status'), get: (r) => t(`studentStatus.${r.status}`) },
            ]}
          />
      </TableToolbar>

      <SectionCard title={t('common.list')} icon={IconUsers} p={0}>
        {isLoading ? (
          <TableSkeleton rows={6} cols={5} />
        ) : !students.length ? (
          <EmptyState icon={IconUsers} title={t('pages.students.empty')} description={t('pages.students.emptyDesc')} />
        ) : (
          <Table.ScrollContainer minWidth={640} pos="relative">
            <FetchOverlay visible={fetching} />
            <Table highlightOnHover verticalSpacing="sm" stickyHeader>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t('fields.fullName')}</Table.Th>
                  <Table.Th>{t('fields.class')}</Table.Th>
                  <Table.Th>{t('fields.phone')}</Table.Th>
                  <Table.Th>{t('studentsPage.colParentPhone')}</Table.Th>
                  <Table.Th>{t('fields.status')}</Table.Th>
                  <Table.Th w={90}></Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {students.map((s) => (
                  <Table.Tr key={s.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/students/${s.id}`)}>
                    <Table.Td>
                      <Group gap="xs" wrap="nowrap">
                        <Avatar size={32} radius="xl" color="brand" src={fileUrl(s.photo)} alt={s.fullName}>
                          {s.fullName[0]?.toUpperCase()}
                        </Avatar>
                        <Text fw={500} size="sm" lineClamp={1}>{s.fullName}</Text>
                      </Group>
                    </Table.Td>
                    <Table.Td>{s.class?.name ?? '—'}</Table.Td>
                    <Table.Td>{s.phone ?? '—'}</Table.Td>
                    <Table.Td>{s.parentPhone ?? '—'}</Table.Td>
                    <Table.Td>
                      <Badge variant="light" color={s.status === 'ACTIVE' ? 'teal' : 'gray'}>
                        {t(`studentStatus.${s.status}`)}
                      </Badge>
                    </Table.Td>
                    <Table.Td onClick={(e) => e.stopPropagation()}>
                      {canContracts && <ContractButton studentId={s.id} />}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
      </SectionCard>
      <TablePagination
        page={page}
        pages={pages}
        total={total}
        limit={limit}
        onPage={setPage}
        onLimit={setLimit}
      />

      <Modal opened={opened} onClose={() => { resetAll(); close(); }} title={t('pages.students.add')} size="md">
        <form onSubmit={form.onSubmit((v) => createMut.mutate(v))}>
          <Stack>
            {pending.length > 0 && (
              <>
                <Select
                  label={t('studentsPage.fromAdmission')}
                  description={t('studentsPage.fromAdmissionDesc')}
                  placeholder={t('studentsPage.fromAdmissionPlaceholder')}
                  leftSection={<IconUserPlus size={16} />}
                  data={pending.map((a) => ({ value: a.id, label: `${a.applicantName} · ${a.parentPhone}` }))}
                  value={admissionId}
                  onChange={pickAdmission}
                  searchable
                  clearable
                />
                {admissionId && (
                  <Alert color="blue" variant="light" py="xs">
                    {t('studentsPage.admissionNotice')}
                  </Alert>
                )}
                <Divider label={t('studentsPage.orManual')} labelPosition="center" />
              </>
            )}

            <TextInput label={t('fields.fullName')} required {...form.getInputProps('fullName')} />
            <CreatableSelect
              label={t('fields.class')}
              required
              data={(classes ?? []).map((c) => ({ value: c.id, label: c.name }))}
              value={form.values.classId}
              onChange={(v) => form.setFieldValue('classId', v || '')}
              error={form.errors.classId}
              createTitle={t('studentsPage.newClass')}
              renderCreate={(onCreated) => <QuickClassForm onCreated={onCreated} />}
            />
            <Group grow>
              <GenderInput {...form.getInputProps('gender')} />
              <DateInput label={t('studentsPage.birthDate')} {...uzDateProps} {...form.getInputProps('birthDate')} />
            </Group>
            <MultiPhoneEditor value={form.values.phones} onChange={(v) => form.setFieldValue('phones', v)} />
            <MultiAddressEditor value={form.values.addresses} onChange={(v) => form.setFieldValue('addresses', v)} />
            <ServerCreatableSelect
              label={t('studentsPage.parentAccount')}
              placeholder={t('studentsPage.parentSearchPlaceholder')}
              endpoint="/students/parents"
              mapOption={(pp: { id: string; fullName: string; phone?: string }) => ({ value: pp.id, label: `${pp.fullName}${pp.phone ? ` · ${pp.phone}` : ''}` })}
              value={form.values.parentId}
              onChange={(v) => form.setFieldValue('parentId', v || '')}
              createTitle={t('studentsPage.newParent')}
              renderCreate={(onCreated) => <QuickParentForm onCreated={onCreated} />}
            />
            <Group grow>
              <TextInput label={t('studentsPage.parentNameNoAccount')} {...form.getInputProps('parentName')} />
              <PhoneInput label={t('fields.parentPhone')} {...form.getInputProps('parentPhone')} />
            </Group>
            <Group justify="flex-end" mt="sm">
              <Button variant="default" onClick={() => { resetAll(); close(); }}>{t('common.cancel')}</Button>
              <Button type="submit" loading={createMut.isPending}>{t('common.save')}</Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </>
  );
}
